import { env } from "@/env";
import { COMMON_FETCH_HEADERS } from "./utils";
import { redirectToSignIn } from "./auth";

type VolumeApiMethod = "GET" | "POST" | "PUT";
type VolumeAcceptHeader =
  | "application/vnd.volume.v0.7+json"
  | "application/vnd.volume.v0.8+json"
  | "application/json";

export const VOLUME_API_VERSION_HEADERS = {
  v07: "application/vnd.volume.v0.7+json",
  v08: "application/vnd.volume.v0.8+json",
  v1: "application/vnd.volume.v1.0+json",
} as const;

class VolumeApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public endpoint: string,
    public method: string,
    message?: string,
  ) {
    super(message);
    this.name = "VolumeApiError";
  }
}

/**
 * Creates headers for Volume API requests
 */
export const createVolumeHeaders = ({
  accessToken,
  acceptHeader,
  applicationId,
  contentTypeHeader,
}: {
  accessToken: string;
  acceptHeader?: VolumeAcceptHeader;
  applicationId?: string;
  contentTypeHeader?: string;
}) => ({
  ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
  ...(acceptHeader && { Accept: acceptHeader }),
  ...(contentTypeHeader && { "Content-Type": contentTypeHeader }),
  ...(applicationId && { "x-application-id": applicationId }),
  ...COMMON_FETCH_HEADERS,
});

/**
 * Creates a query string from parameters
 */
export const createQueryString = (
  params: Record<string, string | number | boolean | undefined>,
) => {
  return new URLSearchParams(
    Object.entries(params)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value !== undefined && value !== null)
      .reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: String(value),
        }),
        {},
      ),
  ).toString();
};

/**
 * Base fetch function for Volume API requests
 */
export const volumeApiFetch = async ({
  endpoint,
  method,
  queryParameters = {},
  headers,
  body,
}: {
  endpoint: string;
  method: VolumeApiMethod;
  queryParameters?: Record<string, string | number | boolean | undefined>;
  headers: HeadersInit;
  body?: object;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): Promise<any> => {
  const queryString = createQueryString(queryParameters);
  const url = `${env.VITE_VOLUME_URL}/api/${endpoint}${queryString ? `?${queryString}` : ""}`;

  try {
    const response = await fetch(url, {
      method,
      headers,
      ...(body && { body: JSON.stringify(body) }),
    });

    if (response.status === 401) {
      throw redirectToSignIn(false);
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new VolumeApiError(
        response.status,
        response.statusText,
        endpoint,
        method,
        errorText,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof VolumeApiError) {
      throw error;
    }
    throw new VolumeApiError(
      500,
      "Internal Error",
      endpoint,
      method,
      (error as Error).message,
    );
  }
};

/**
 * Helper to remove 'Z' from datetime strings for consistency
 */
export const formatDateTimeForApi = (dateTime: string) =>
  dateTime.replace("Z", "");

/**
 * Helper to create application-specific query parameters
 */
export const withApplicationId = (
  params: Record<string, string | number | boolean | undefined>,
  applicationId: string,
) => ({
  ...params,
  ...(applicationId && { applicationId }),
});
